import { Box, Avatar, Typography, IconButton, Tooltip, Stack } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';

const colors = {
  primary: '#003ec7',
  onPrimary: '#ffffff',
  onSurface: '#0b1c30',
  outline: '#737688',
  outlineVariant: '#c3c5d9',
  error: '#ba1a1a',
};

export interface UserProfileProps {
  /**
   * User's display name
   */
  name: string;

  /**
   * User's email address
   */
  email: string;

  /**
   * Optional avatar URL
   * If not provided, will show initials instead
   */
  avatarUrl?: string;

  /**
   * Callback when logout is clicked
   */
  onLogout: () => void;

  /**
   * Compact mode (hides email, smaller size)
   */
  compact?: boolean;
}

/**
 * User Profile Component
 *
 * Displays user information with avatar, name, email, and logout button.
 * Design aligned with Stitch: Nexus Admin design system.
 */
export function UserProfile({
  name,
  email,
  avatarUrl,
  onLogout,
  compact = false,
}: UserProfileProps) {
  /**
   * Generate user initials from name
   * Takes first letter of first two words
   */
  const getInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Stack
      direction="row"
      sx={{
        p: 2,
        gap: 1.5,
        alignItems: 'center',
        borderTop: `1px solid ${colors.outlineVariant}`,
      }}
    >
      <Avatar
        src={avatarUrl}
        alt={name}
        sx={{
          width: 40,
          height: 40,
          backgroundColor: colors.primary,
          color: colors.onPrimary,
          fontSize: '14px',
          fontWeight: 700,
        }}
      >
        {getInitials(name)}
      </Avatar>
      <Box sx={{ mr: 'auto', minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '13px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            color: colors.onSurface,
            lineHeight: '18px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </Typography>
        {!compact && (
          <Typography
            sx={{
              fontSize: '11px',
              fontFamily: 'Inter, sans-serif',
              color: colors.outline,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {email}
          </Typography>
        )}
      </Box>
      <Tooltip title="Logout">
        <IconButton
          size="small"
          onClick={onLogout}
          sx={{
            color: colors.outline,
            '&:hover': {
              color: colors.error,
              backgroundColor: 'rgba(186, 26, 26, 0.05)',
            },
          }}
        >
          <LogoutIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
